/* PLW-036 sticky add-to-cart bar. Mirrors and submits the MAIN product form; no cart logic of its own. */
if (!customElements.get('plw-sticky-atc')) {
  customElements.define(
    'plw-sticky-atc',
    class PlwStickyAtc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-plw-sticky-button]');
        this.label = this.querySelector('[data-plw-sticky-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.variantEl = this.querySelector('[data-plw-sticky-variant]');
        this.priceEl = this.querySelector('[data-plw-sticky-price]');
        this.singleVariant = this.dataset.singleVariant === 'true';
        this.isShown = false;

        try {
          const json = this.querySelector('[data-plw-sticky-variants]');
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (e) {
          this.variants = [];
        }

        this.onClick = this.onClick.bind(this);
        this.onIdChange = this.onIdChange.bind(this);
        this.updateOffset = this.updateOffset.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.button.addEventListener('click', this.onClick);
        window.addEventListener('resize', this.updateOffset, { passive: true });
        // Fallback for jumps (anchor links, restored scroll) that skip past the button without an intersection change.
        window.addEventListener('scroll', this.onScroll, { passive: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Quick-add modals publish this event too, so always read the MAIN form's
            // input[name=id] rather than trusting the payload. Re-bind in case the
            // main section was re-rendered.
            this.bindMain();
            this.syncFromForm();
          });
        }

        this.bindMain();
        this.syncFromForm();
      }

      disconnectedCallback() {
        this.button && this.button.removeEventListener('click', this.onClick);
        window.removeEventListener('resize', this.updateOffset);
        window.removeEventListener('scroll', this.onScroll);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        this.teardownMain();
        this.setShown(false);
      }

      /* ---------- main form wiring ---------- */

      findMainForm() {
        const info =
          document.querySelector('product-info[id^="MainProduct-"]') ||
          document.querySelector('product-info[data-section]');
        const scope = info || document;
        return (
          scope.querySelector('product-form form[data-type="add-to-cart-form"]') ||
          scope.querySelector('form[id^="product-form-"]:not([id^="product-form-installment"])')
        );
      }

      bindMain() {
        const form = this.findMainForm();
        const submit = form ? form.querySelector('.product-form__submit') : null;
        if (form === this.mainForm && submit === this.mainButton) return;

        this.teardownMain();
        this.mainForm = form;
        this.mainButton = submit;

        if (!form || !submit) {
          // No main product form on this page (e.g. product not found): never show the bar.
          this.setShown(false);
          return;
        }

        // Variant id input changes (also covers "unavailable" combos, which don't publish variantChange).
        form.addEventListener('change', this.onIdChange);

        // Mirror disabled / sold out / loading state of the real button.
        this.buttonObserver = new MutationObserver(() => this.mirrorButton());
        this.buttonObserver.observe(submit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
        this.mirrorButton();

        // Show the bar only after the real Add to cart button has scrolled up out of view.
        if ('IntersectionObserver' in window) {
          this.intersection = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
                this.setShown(scrolledPast);
              });
            },
            { threshold: 0 }
          );
          this.intersection.observe(submit);
        }
      }

      teardownMain() {
        if (this.mainForm) this.mainForm.removeEventListener('change', this.onIdChange);
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (this.intersection) this.intersection.disconnect();
        this.buttonObserver = null;
        this.intersection = null;
        this.mainForm = null;
        this.mainButton = null;
      }

      onIdChange(event) {
        if (event.target && event.target.name === 'id') this.syncFromForm();
      }

      syncFromForm() {
        if (!this.mainForm) return;
        const input = this.mainForm.querySelector('input[name="id"], select[name="id"]');
        this.renderVariant(input ? input.value : null);
      }

      /* ---------- rendering ---------- */

      renderVariant(id) {
        const variant = id ? this.variants.find((v) => String(v.id) === String(id)) : null;

        if (this.variantEl) {
          const showTitle = !!variant && !this.singleVariant;
          this.variantEl.textContent = showTitle ? variant.title : '';
          this.variantEl.hidden = !showTitle;
        }

        if (this.priceEl) {
          this.priceEl.hidden = !variant;
          this.priceEl.textContent = '';
          if (variant) {
            if (variant.compare) {
              this.priceEl.append(this.hiddenText(this.dataset.regularLabel));
              const s = document.createElement('s');
              s.className = 'plw-sticky-atc__compare';
              s.textContent = variant.compare;
              this.priceEl.append(s, this.hiddenText(this.dataset.saleLabel));
            }
            const current = document.createElement('span');
            current.className = 'plw-sticky-atc__current';
            current.textContent = variant.price;
            this.priceEl.append(current);
          }
        }

        this.mirrorButton();
      }

      hiddenText(text) {
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = text || '';
        return span;
      }

      mirrorButton() {
        const main = this.mainButton;
        if (!main || !this.button) return;

        const disabled = main.hasAttribute('disabled');
        const loading = main.classList.contains('loading');
        this.button.toggleAttribute('disabled', disabled);
        this.button.classList.toggle('loading', loading);
        if (loading) this.button.setAttribute('aria-disabled', 'true');
        else this.button.removeAttribute('aria-disabled');
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);

        // Label mirrors the real button: "Add to cart" / "Sold out" / "Unavailable".
        const mainLabel = main.querySelector('span:not(.hidden):not(.visually-hidden)');
        const text = mainLabel ? mainLabel.textContent.trim() : '';
        if (text && this.label && this.label.textContent.trim() !== text) this.label.textContent = text;
      }

      /* ---------- visibility ---------- */

      onScroll() {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.scrollTicking = false;
          if (!this.mainButton || !this.isConnected) return;
          const scrolledPast = this.mainButton.getBoundingClientRect().bottom <= 0;
          if (scrolledPast !== this.isShown) this.setShown(scrolledPast);
        });
      }

      setShown(show) {
        this.isShown = !!show;
        this.classList.toggle('is-visible', this.isShown);
        if (this.isShown) {
          this.removeAttribute('aria-hidden');
          this.removeAttribute('inert');
        } else {
          this.setAttribute('aria-hidden', 'true');
          this.setAttribute('inert', '');
        }
        this.updateOffset();
      }

      updateOffset() {
        const height = this.isShown ? this.offsetHeight : 0;
        const root = document.documentElement;
        if (height > 0) {
          root.style.setProperty('--plw-sticky-atc-height', `${height}px`);
          document.body.classList.add('plw-sticky-atc-visible');
        } else {
          root.style.removeProperty('--plw-sticky-atc-height');
          document.body.classList.remove('plw-sticky-atc-visible');
        }
      }

      /* ---------- action ---------- */

      onClick(event) {
        event.preventDefault();
        this.bindMain();
        const form = this.mainForm;
        const submit = this.mainButton;
        if (!form || !submit) return;
        if (submit.hasAttribute('disabled') || submit.getAttribute('aria-disabled') === 'true') return;

        // Submit the real form so product-form.js handles variant, quantity, properties and the cart drawer.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submit);
        } else {
          submit.click();
        }
      }
    }
  );
}
